import { useEffect, useState } from 'react'
import { Drawer, Empty, Pagination, Spin, Tag, Typography, message } from 'antd'
import { FileSearchOutlined } from '@ant-design/icons'
import { api } from '../api'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import { formatAttrKv, type SelectedAttribute } from '../lib/attributeSearch'
import { knowledgeDocPreviewPath } from '../lib/knowledgeDocPreview'
import { listPagination, useServerPagination } from './ui'
import './AttributeSearchDrawer.css'

type AttrSearchItem = {
  item_id: number
  title: string
  matched_attributes: string[]
  matched_count: number
  required_count: number
}

type AttrSearchRes = {
  scope: KnowledgeScope
  attributes: SelectedAttribute[]
  match_mode: 'all'
  items: AttrSearchItem[]
  total: number
  page: number
  page_size: number
  response_ms: number
}

type Props = {
  open: boolean
  onClose: () => void
  scope: KnowledgeScope
  attributes: SelectedAttribute[]
  excludeItemId?: number
  sourceQuery?: string
}

export function AttributeSearchDrawer({
  open,
  onClose,
  scope,
  attributes,
  excludeItemId,
  sourceQuery = '',
}: Props) {
  const { page, pageSize, handlePaginationChange, paginationOptions, resetPage } = useServerPagination()
  const [loading, setLoading] = useState(false)
  const [res, setRes] = useState<AttrSearchRes | null>(null)

  useEffect(() => {
    if (!open) return
    resetPage()
  }, [open, attributes, scope, excludeItemId, resetPage])

  useEffect(() => {
    if (!open || attributes.length === 0) {
      setRes(null)
      return
    }
    setLoading(true)
    api
      .post<AttrSearchRes>('/public/search/by-attributes', {
        scope,
        attributes,
        exclude_item_id:
          excludeItemId != null && Number.isFinite(excludeItemId) && excludeItemId > 0
            ? excludeItemId
            : undefined,
        page,
        page_size: pageSize,
      })
      .then((r) => setRes(r.data))
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }, [open, scope, attributes, excludeItemId, page, pageSize])

  const total = res?.total ?? 0
  const items = res?.items ?? []

  const openDoc = (hit: AttrSearchItem) => {
    window.open(
      knowledgeDocPreviewPath(hit.item_id, scope, sourceQuery, {
        highlightKvs: hit.matched_attributes,
        fromItemId: excludeItemId,
      }),
      '_blank',
      'noopener,noreferrer',
    )
  }

  return (
    <Drawer
      className="attr-search-drawer"
      title={
        <span>
          <FileSearchOutlined /> 同属性文档
        </span>
      }
      placement="right"
      open={open}
      onClose={onClose}
      size={Math.min(400, typeof window !== 'undefined' ? window.innerWidth - 48 : 400)}
      destroyOnClose
      mask={false}
      rootClassName="attr-search-drawer-root"
    >
      <div className="attr-search-drawer-criteria">
        <Typography.Text type="secondary" className="attr-search-drawer-criteria-label">
          须同时满足所选属性：
        </Typography.Text>
        <div className="attr-search-drawer-criteria-tags">
          {attributes.map((a) => (
            <Tag key={formatAttrKv(a)} color="gold">
              {formatAttrKv(a)}
            </Tag>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="attr-search-drawer-loading">
          <Spin description="检索中…" />
        </div>
      ) : attributes.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="请先选择属性" />
      ) : items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未找到同时满足所选属性的文档" />
      ) : (
        <>
          <div className="attr-search-drawer-meta">
            <Tag color="blue">共 {total} 篇</Tag>
            {res ? <span>耗时 {res.response_ms} ms</span> : null}
            <Tag color="gold">同属性 AND</Tag>
          </div>
          <ul className="attr-search-drawer-list">
            {items.map((hit) => (
              <li key={hit.item_id}>
                <button type="button" className="attr-search-drawer-item" onClick={() => openDoc(hit)}>
                  <span className="attr-search-drawer-item-title">{hit.title}</span>
                  <span className="attr-search-drawer-item-match">
                    匹配 {hit.matched_count}/{hit.required_count}
                  </span>
                  <div className="attr-search-drawer-item-tags">
                    {hit.matched_attributes.map((kv) => (
                      <Tag key={kv} color="gold" className="attr-search-drawer-item-tag">
                        {kv}
                      </Tag>
                    ))}
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {total > pageSize ? (
            <div className="attr-search-drawer-pagination">
              <Pagination
                {...listPagination(page, total, handlePaginationChange, { unit: '篇', ...paginationOptions })}
              />
            </div>
          ) : null}
        </>
      )}
    </Drawer>
  )
}
